/**
 * Provides the event names used by @paychex/state. Consumers can
 * import these names to attach their own event handlers.
 *
 * ```js
 * // esm
 * import { Events } from '@paychex/state';
 *
 * // cjs
 * const { Events } = require('@paychex/state');
 *
 * // iife
 * const { Events } = window['@paychex/state'];
 *
 * // amd
 * require(['@paychex/state'], function({ Events }) { ... });
 * define(['@paychex/state'], function({ Events }) { ... });
 * ```
 *
 * @module Events
 */

/**
 * Fired when a {@link Batch} transaction has committed successfully.
 * Listen to this event to update the UI or persist changes to a Store
 * or backend.
 *
 * @example
 * ```js
 * model.on(Events.COMMIT, async function saveChanges(snapshot) {
 *   const id = snapshot.id;
 *   await store.set(id, snapshot);
 *   const request = data.createRequest(save, { id });
 *   await data.fetch(request).catch(errors.rethrow({ id }));
 * });
 * ```
 */
export const COMMIT = 'commit'

/**
 * Fires when the state instance is disposed. Listen to this
 * event to clean up any handlers or references.
 *
 * @example
 * ```js
 * const listeners = [
 *   model.on(Events.COMMIT, saveChanges),
 *   model.on('custom-event', performAction),
 *   model.on(Events.DISPOSE, () => {
 *     model = null;
 *     listeners.forEach(off => off());
 *   }),
 * ];
 * ```
 */
export const DISPOSE = 'dispose'

/**
 * Fired when a {@link Batch} transaction fails.
 *
 * @example
 * ```js
 * model.on(Events.ROLLBACK, function trackFailure(reason) {
 *   tracker.event('model changes failed', { reason });
 * });
 * ```
 */
export const ROLLBACK = 'rollback'

/**
 * Fired between a {@link Broker} and the {@link Exchange} when
 * a broker needs to convert a snapshot from one version to another.
 * You will only need to fire this event if you are writing a
 * Broker that needs this functionality.
 *
 * @example
 * ```js
 * const results = await bus.fire(Events.CONVERT, key, from, to, snapshot);
 * const result = results.find(Boolean);
 * if (result) {
 *   const [version, data] = result;
 * }
 * ```
 */
export const CONVERT = 'convert'

/**
 * @ignore
 */
export const UPDATE = 'update'

/**
 * Fired between the {@link Exchange} and any {@link Broker Brokers} when
 * a new state instance has been requested. Brokers can listen for
 * this event if they wish to provide a versioned snapshot back to
 * the Exchange.
 *
 * @example
 * ```js
 * bus.on(Events.GET, async (key, pattern) => {
 *   if (utils.satisfies('1.0.0', pattern))
 *     return ['1.0.0', myStateSnapshot];
 * });
 * ```
 */
export const GET = 'get'

/**
 * Fired between the {@link Exchange} and any {@link Broker Brokers} when
 * a new state instance has been published. Brokers can use the
 * key, version, and snapshot as they need.
 *
 * @example
 * ```js
 * bus.on(Events.SET, (key, version, snapshot) => {
 *   // do something here
 * });
 * ```
 */
export const SET = 'set'
